﻿import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';

import { configureApiClient } from '@/api/apiClient';
import { fetchClients, fetchCurrentClient } from './clientApi';
import { ClientContext } from './ClientContext';
import type { Client } from './types';
import { showErrorToast } from '@/errors/showErrorToast';

const SELECTED_CLIENT_STORAGE_KEY = 'orbit:selectedClientId';

/**
 * Resolves which client should be active on startup given a list of accessible
 * clients, an optional deep-link (URL) client ID, and an optional persisted client ID.
 *
 * Priority:
 *   1. URL/deep-link client ID, if it exists in the accessible list (so a pasted
 *      `?client=` link loads that client even on a cold start).
 *   2. Persisted client ID, if it still exists in the accessible list.
 *   3. The client flagged as `isCurrentClient` by the API.
 *   4. The first accessible client as a fallback.
 */
function resolveSelectedClient(
    clients: Client[],
    urlClientId: string | null,
    persistedClientId: string | null,
): Client | null {
    if (clients.length === 0) return null;

    if (urlClientId) {
        const fromUrl = clients.find((c) => c.id === urlClientId);
        if (fromUrl) return fromUrl;
    }

    if (persistedClientId) {
        const persisted = clients.find((c) => c.id === persistedClientId);
        if (persisted) return persisted;
    }

    const current = clients.find((c) => c.isCurrentClient);
    if (current) return current;

    return clients[0];
}

interface ClientProviderProps {
    children: ReactNode;
    /** API base URL from AppConfig — used to configure the shared axios instance. */
    apiBaseUrl: string;
    /**
     * Token acquisition function from AuthContext. Called by the axios interceptor
     * before every authenticated API request.
     */
    getAccessToken: () => Promise<string>;
    /**
     * Client ID from the URL `?client=` param at bootstrap, if any. Takes priority over
     * the persisted selection so a pasted deep link loads that client on a cold start.
     * Read once at mount; subsequent client changes flow through `switchClient`.
     */
    initialClientId?: string;
}

/**
 * Provides multi-tenant client context to the application.
 *
 * Configures the shared axios instance on mount (interceptor + base URL), fetches
 * the list of accessible clients, resolves the active client using persisted
 * selection → API current flag → first client fallback, and exposes `switchClient`
 * to change the active client without a page reload.
 *
 * Must be rendered inside `<AuthProvider>` so that `getAccessToken` is available.
 */
export function ClientProvider({ children, apiBaseUrl, getAccessToken, initialClientId }: ClientProviderProps) {
    const [persistedClientId, setPersistedClientId] = useLocalStorage<string | null>(SELECTED_CLIENT_STORAGE_KEY, null);
    const [clients, setClients] = useState<Client[]>([]);
    const [selectedClient, setSelectedClient] = useState<Client | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [isOriginUser, setIsOriginUser] = useState(false);
    const [canViewVehicleTracking, setCanViewVehicleTracking] = useState(false);
    const [canViewNetworkExplorer, setCanViewNetworkExplorer] = useState(false);

    useEffect(() => {
        // Configure the shared axios instance before the first API call.
        // Both steps are inside the same effect to guarantee ordering: the
        // interceptor is registered before fetchClients fires.
        configureApiClient(apiBaseUrl, getAccessToken);

        let cancelled = false;

        async function load() {
            setIsLoading(true);
            setIsError(false);
            try {
                const currentClient = await fetchCurrentClient();

                let clientList: Client[];
                if (currentClient.isOriginUser) {
                    clientList = await fetchClients();
                } else {
                    clientList = [
                        {
                            id: currentClient.id,
                            name: currentClient.name,
                            modules: currentClient.modules,
                            logoUrl: currentClient.logoUrl,
                            isCurrentClient: true,
                            focalPoint: currentClient.focalPoint,
                            languageCode: currentClient.languageCode,
                        },
                    ];
                }

                if (cancelled) return;
                setIsOriginUser(currentClient.isOriginUser);
                setCanViewVehicleTracking(currentClient.canViewVehicleTracking);
                setCanViewNetworkExplorer(currentClient.canViewNetworkExplorer);
                setClients(clientList);
                const resolved = resolveSelectedClient(clientList, initialClientId ?? null, persistedClientId);
                setSelectedClient(resolved);
                // Persist the resolved ID so future loads start from the right client
                if (resolved) setPersistedClientId(resolved.id);
            } catch (err) {
                if (cancelled) return;
                setIsError(true);
                showErrorToast(err);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        }

        void load();

        return () => {
            cancelled = true;
        };
        // Run once on mount. persistedClientId is intentionally read only at
        // load time — subsequent updates come through switchClient.
        // apiBaseUrl and getAccessToken are stable references (AppConfig + useCallback).
        // eslint-disable-next-line react-hooks/exhaustive-deps, react-x/exhaustive-deps
    }, []);

    const switchClient = useCallback(
        (clientId: string) => {
            const target = clients.find((c) => c.id === clientId);
            if (!target) return;
            setSelectedClient(target);
            setPersistedClientId(target.id);
        },
        [clients, setPersistedClientId],
    );

    // A vehicle request answering 403 means the permission was revoked server-side;
    // flipping the capability hides the layer toggle and stops all vehicle polling.
    const revokeVehicleTracking = useCallback(() => {
        setCanViewVehicleTracking(false);
    }, []);

    const value = useMemo(
        () => ({
            clients,
            selectedClient,
            isLoading,
            isError,
            isOriginUser,
            canViewVehicleTracking,
            revokeVehicleTracking,
            canViewNetworkExplorer,
            switchClient,
        }),
        [
            clients,
            selectedClient,
            isLoading,
            isError,
            isOriginUser,
            canViewVehicleTracking,
            revokeVehicleTracking,
            canViewNetworkExplorer,
            switchClient,
        ],
    );

    return <ClientContext value={value}>{children}</ClientContext>;
}
